import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ClipboardCheck,
  ArrowRight,
  ArrowLeft,
  Send,
  Loader2,
  CheckCircle2,
  Clock,
  Sparkles,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMyPendingSurveys, useRespondSurvey } from '@/hooks/useSurveys';
import { useAuthStore } from '@/stores/auth';
import type { Survey, SurveyQuestion } from '@/services/surveys.service';

/**
 * "Responder depois" e o X ficam lembrados por uma semana, por pesquisa.
 * Antes a escolha morava só na memória da aba: cada recarga abria o pop-up de
 * novo por cima da tela, e ele aparecia "toda hora".
 */
const CHAVE_ADIADAS = 'devcourse-pesquisas-adiadas';
const SEMANA = 7 * 24 * 60 * 60 * 1000;

function adiadasAgora(): Set<string> {
  try {
    const salvas = JSON.parse(
      localStorage.getItem(CHAVE_ADIADAS) || '{}',
    ) as Record<string, number>;
    return new Set(
      Object.keys(salvas).filter((id) => Date.now() - salvas[id] < SEMANA),
    );
  } catch {
    return new Set();
  }
}

function adiar(id: string) {
  try {
    const salvas = JSON.parse(
      localStorage.getItem(CHAVE_ADIADAS) || '{}',
    ) as Record<string, number>;
    salvas[id] = Date.now();
    localStorage.setItem(CHAVE_ADIADAS, JSON.stringify(salvas));
  } catch {
    /* navegação privada: vale só nesta aba */
  }
}

// Event bus for programmatic triggers
type SurveyTriggerCallback = (ctx: {
  type: string;
  lessonId?: string;
  moduleId?: string;
  productId?: string;
}) => void;
const listeners: SurveyTriggerCallback[] = [];
let welcomeContext: { productId: string; pathname: string } | undefined;
export function onSurveyTrigger(cb: SurveyTriggerCallback) {
  listeners.push(cb);
  return () => {
    const i = listeners.indexOf(cb);
    if (i >= 0) listeners.splice(i, 1);
  };
}
export function triggerSurvey(ctx: {
  type: string;
  lessonId?: string;
  moduleId?: string;
  productId?: string;
}) {
  if (ctx.type === 'course_first_access' && ctx.productId) welcomeContext = { productId: ctx.productId, pathname: window.location.pathname };
  listeners.forEach((cb) => cb(ctx));
}

export function SurveyModal() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const [courseContext, setCourseContext] = useState(welcomeContext);
  const productId = courseContext?.pathname === location.pathname ? courseContext.productId : undefined;
  const { data: pending = [], refetch } = useMyPendingSurveys(productId);
  const respondMutation = useRespondSurvey();

  const [activeSurvey, setActiveSurvey] = useState<Survey | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(adiadasAgora);
  // a pesquisa é para o aluno: não abre por cima do painel do admin
  const noAdmin = location.pathname.startsWith('/admin');
  const [currentStep, setCurrentStep] = useState(0); // 0 = intro, 1..n = questions, n+1 = submitting/done
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [phase, setPhase] = useState<'intro' | 'questions' | 'done'>('intro');

  // Auto-show pending survey
  useEffect(() => {
    if (!isAuthenticated || noAdmin || activeSurvey || phase === 'done') return;
    const next = pending.find((s) => !dismissed.has(s.id));
    if (next) {
      const t = setTimeout(() => {
        setActiveSurvey(next);
        setCurrentStep(0);
        setPhase('intro');
        setAnswers({});
      }, 1000);
      return () => clearTimeout(t);
    }
  }, [pending, isAuthenticated, noAdmin, dismissed, activeSurvey, phase]);

  useEffect(() => {
    return onSurveyTrigger((context) => {
      if (context.type === 'course_first_access' && context.productId) {
        setCourseContext({ productId: context.productId, pathname: pathRef.current });
        setPhase('intro');
      } else void refetch();
    });
  }, [refetch]);

  useEffect(() => {
    if (!productId && activeSurvey?.triggers?.some((trigger) => trigger.type === 'course_first_access')) {
      setActiveSurvey(null);
      setPhase('intro');
    }
  }, [productId, activeSurvey]);

  const questions = useMemo(
    () => activeSurvey?.questions || [],
    [activeSurvey],
  );
  const totalSteps = questions.length;
  const xpReward = activeSurvey?.xpReward ?? 10;
  const currentQuestion =
    phase === 'questions' ? questions[currentStep - 1] : null;
  const currentAnswer = currentQuestion ? (answers[currentQuestion.id] ?? '').trim() : '';
  const currentAnswerValid = !currentQuestion || ((!currentQuestion.required || !!currentAnswer) && (currentQuestion.type !== 'email' || !currentAnswer || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentAnswer)));
  const progress =
    phase === 'intro'
      ? 0
      : phase === 'done'
        ? 100
        : totalSteps === 0
          ? 0
          : Math.round(((currentStep - 1) / totalSteps) * 100);

  const handleDismiss = useCallback(() => {
    if (activeSurvey) {
      adiar(activeSurvey.id);
      setDismissed((prev) => new Set(prev).add(activeSurvey.id));
    }
    setActiveSurvey(null);
    setPhase('intro');
  }, [activeSurvey]);

  const handleNext = useCallback(() => {
    if (phase === 'intro') {
      setPhase('questions');
      setCurrentStep(1);
      return;
    }
    if (currentStep < totalSteps) {
      setCurrentStep((s) => s + 1);
    }
  }, [phase, currentStep, totalSteps]);

  const handlePrev = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1);
    } else {
      setPhase('intro');
      setCurrentStep(0);
    }
  }, [currentStep]);

  const handleSubmit = useCallback(async () => {
    if (!activeSurvey) return;
    setSubmitError('');
    const answerList = Object.entries(answers)
      .filter(([, v]) => v)
      .map(([questionId, value]) => ({ questionId, value }));
    try {
      const welcome = activeSurvey.triggers?.some((trigger) => trigger.type === 'course_first_access');
      await respondMutation.mutateAsync({ surveyId: activeSurvey.id, answers: answerList, ...(welcome && productId ? { triggerType: 'course_first_access', triggerContext: { productId } } : {}) });
    } catch {
      setSubmitError('Não foi possível enviar sua resposta. Verifique os campos e tente novamente.');
      return;
    }
    setPhase('done');
    setTimeout(() => {
      setActiveSurvey(null);
      setPhase('intro');
      setAnswers({});
    }, 3000);
  }, [activeSurvey, answers, respondMutation, productId]);

  const isLastQuestion = currentStep === totalSteps && phase === 'questions';

  if (!activeSurvey) return null;

  return (
    <Dialog open={!!activeSurvey} onOpenChange={(v) => !v && handleDismiss()}>
      <DialogContent className="bg-card w-[calc(100vw-32px)] max-w-lg gap-0 overflow-hidden rounded-xl p-0 sm:rounded-xl">
        <DialogTitle className="sr-only">{activeSurvey.title}</DialogTitle>
        <DialogDescription className="sr-only">
          Pesquisa com {totalSteps} perguntas. Você pode responder agora ou
          depois.
        </DialogDescription>
        {/* Progress bar — step + bar + percent */}
        {phase !== 'intro' && (
          <div className="bg-card border-border relative space-y-2 border-b pb-4 pl-6 pr-14 pt-5">
            <div className="flex items-center justify-between text-xs font-medium">
              <span className="text-muted-foreground inline-flex items-center gap-1.5">
                <ClipboardCheck className="h-3 w-3" />
                {phase === 'done'
                  ? 'Concluído'
                  : `Etapa ${currentStep} de ${totalSteps}`}
              </span>
              <span className="text-primary text-sm">{progress}%</span>
            </div>
            <div className="bg-muted relative h-1.5 w-full overflow-hidden rounded-full">
              <div
                className="from-primary to-devclub-green-light relative h-full rounded-full bg-gradient-to-r shadow-[0_0_12px_rgba(163,230,53,0.5)] transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* INTRO */}
        {phase === 'intro' && (
          <div className="relative overflow-hidden">
            {/* Decorative gradient background */}
            <div className="from-primary/10 via-primary/5 pointer-events-none absolute inset-0 bg-gradient-to-br to-transparent" />
            <div className="from-primary/0 via-primary/40 to-primary/0 pointer-events-none absolute left-0 right-0 top-0 h-px bg-gradient-to-r" />

            <div className="relative space-y-5 p-6 text-center">
              {/* Animated icon */}
              <div className="relative mx-auto h-20 w-20">
                <div className="from-primary/30 to-primary/10 absolute inset-0 animate-pulse rounded-2xl bg-gradient-to-br blur-xl" />
                <div className="from-primary/20 to-primary/5 ring-primary/30 relative flex h-full w-full items-center justify-center rounded-2xl bg-gradient-to-br ring-1">
                  <ClipboardCheck className="text-primary h-10 w-10" />
                </div>
                {/* Floating sparkle */}
                <Sparkles className="absolute -right-1 -top-1 h-5 w-5 animate-pulse text-amber-400" />
              </div>

              {/* Surprise tag */}
              <div className="from-primary/10 to-devclub-green-light/10 border-primary/20 inline-flex items-center gap-1.5 rounded-full border bg-gradient-to-r px-3 py-1">
                <Sparkles className="text-primary h-3 w-3" />
                <span className="text-primary text-[10px] font-bold uppercase tracking-wider">
                  Sua opinião importa
                </span>
              </div>

              <div className="space-y-2">
                <h2 className="text-xl font-bold leading-tight sm:text-2xl">
                  {activeSurvey.title}
                </h2>
                {activeSurvey.description ? (
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {activeSurvey.description}
                  </p>
                ) : (
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Responda algumas perguntas rápidas e nos ajude a melhorar
                    sua experiência.
                  </p>
                )}
              </div>

              {/* Info chips */}
              <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
                <span className="bg-secondary/60 border-border/60 inline-flex items-center gap-1 rounded-full border px-3 py-1 font-medium">
                  <ClipboardCheck className="h-3.5 w-3.5" />
                  {totalSteps} {totalSteps === 1 ? 'pergunta' : 'perguntas'}
                </span>
                <span className="bg-secondary/60 border-border/60 inline-flex items-center gap-1 rounded-full border px-3 py-1 font-medium">
                  <Clock className="h-3.5 w-3.5" />~
                  {Math.max(1, Math.ceil(totalSteps * 0.3))} min
                </span>
                {xpReward > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-gradient-to-r from-amber-400/15 to-amber-500/15 px-3 py-1 font-bold text-amber-300">
                    <Zap className="h-3.5 w-3.5 fill-amber-300" />+{xpReward} XP
                  </span>
                )}
              </div>

              {/* CTA */}
              <div className="flex flex-col gap-2 pt-1">
                <Button
                  onClick={handleNext}
                  className="group w-full gap-2"
                  size="lg"
                >
                  Começar agora
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
                <Button variant="ghost" size="sm"
                  type="button"
                  onClick={handleDismiss}

                >
                  Responder depois
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* QUESTIONS (one at a time) */}
        {phase === 'questions' && currentQuestion && (
          <div key={currentQuestion.id} className="p-6 pt-5">
            {/* Step badge */}
            <div className="mb-4 flex items-center gap-2">
              <span className="bg-primary/10 text-primary ring-primary/20 flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ring-1">
                {currentStep}
              </span>
              <div className="bg-border/50 h-px flex-1" />
            </div>

            {/* Question */}
            <div className="min-h-[200px] space-y-5">
              <div className="space-y-1.5">
                <h3 className="text-lg font-bold leading-snug sm:text-xl">
                  {currentQuestion.label}
                  {currentQuestion.required && (
                    <span className="text-destructive ml-1">*</span>
                  )}
                </h3>
                {currentQuestion.description && (
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {currentQuestion.description}
                  </p>
                )}
              </div>

              <QuestionInput
                question={currentQuestion}
                value={answers[currentQuestion.id] || ''}
                onChange={(v) =>
                  setAnswers((p) => ({ ...p, [currentQuestion.id]: v }))
                }
              />
            </div>

            {/* Navigation */}
            {submitError && <p role="alert" className="mt-4 text-sm text-destructive">{submitError}</p>}
            <div className="border-border/40 mt-6 flex items-center justify-between gap-3 border-t pt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrev}
                className="gap-1.5"
              >
                <ArrowLeft className="h-4 w-4" />
                Anterior
              </Button>

              {isLastQuestion ? (
                <Button
                  onClick={handleSubmit}
                  disabled={respondMutation.isPending || !currentAnswerValid}
                  className="gap-2"
                >
                  {respondMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  Enviar resposta
                </Button>
              ) : (
                <Button onClick={handleNext} disabled={!currentAnswerValid} className="gap-1.5">
                  Próxima
                  <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* DONE */}
        {phase === 'done' && (
          <div className="space-y-5 p-6 sm:p-8" role="status">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--success-bg)] text-[var(--success-text)]">
              <CheckCircle2 className="h-6 w-6" aria-hidden />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">Resposta enviada</h2>
              <p className="text-muted-foreground text-sm">
                Obrigado por compartilhar sua opinião.
              </p>
            </div>
            {xpReward > 0 && (
              <p className="text-muted-foreground text-sm">
                Você ganhou +{xpReward} XP.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function QuestionInput({
  question,
  value,
  onChange,
}: {
  question: SurveyQuestion;
  value: string;
  onChange: (v: string) => void;
}) {
  const cfg = question.config || {};
  const type = question.type;

  // Text / Number / Currency
  if (['text', 'email', 'number', 'currency'].includes(type)) {
    return (
      <Input
        type={type === 'text' ? 'text' : type === 'email' ? 'email' : 'number'}
        aria-label={question.label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={question.placeholder || ''}
        autoFocus
      />
    );
  }

  // Textarea
  if (type === 'textarea') {
    return (
      <Textarea
        aria-label={question.label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={question.placeholder || ''}
        rows={4}
        autoFocus
      />
    );
  }

  // Select
  if (type === 'select') {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger  aria-label={question.label}>
          <SelectValue placeholder={question.placeholder || 'Selecione'} />
        </SelectTrigger>
        <SelectContent>
          {(cfg.options || []).map((opt: string) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // NPS / Scale / Rating
  if (['nps', 'scale', 'rating'].includes(type)) {
    const min = cfg.min ?? 0;
    const max = cfg.max ?? (type === 'nps' ? 10 : 5);
    const nums = Array.from({ length: max - min + 1 }, (_, i) => i + min);
    const isNps = type === 'nps';

    return (
      <div className="space-y-2">
        <div className="flex flex-wrap justify-center gap-2">
          {nums.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(String(n))}
              aria-pressed={value === String(n)}
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-md border text-base font-semibold transition-colors',
                value === String(n)
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border hover:border-primary/50 hover:bg-secondary/40',
              )}
            >
              {n}
            </button>
          ))}
        </div>
        {isNps && (
          <div className="text-muted-foreground flex justify-between px-1 text-xs">
            <span>Nada provável</span>
            <span>Muito provável</span>
          </div>
        )}
      </div>
    );
  }

  // Checkbox / Multiselect
  if (['checkbox', 'multiselect'].includes(type)) {
    const selected = value.split(',').filter(Boolean);
    return (
      <div className="space-y-2">
        {(cfg.options || []).map((opt: string) => {
          const isSelected = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              aria-pressed={isSelected}
              onClick={() => {
                const next = isSelected
                  ? selected.filter((s) => s !== opt)
                  : [...selected, opt];
                onChange(next.join(','));
              }}
              className={cn(
                'flex min-h-12 w-full items-center gap-3 rounded-md border px-4 py-3 text-left text-sm font-medium transition-colors',
                isSelected
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border hover:border-primary/50',
              )}
            >
              <div
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-all',
                  isSelected ? 'border-primary bg-primary' : 'border-border',
                )}
              >
                {isSelected && (
                  <CheckCircle2 className="text-primary-foreground h-3.5 w-3.5" />
                )}
              </div>
              {opt}
            </button>
          );
        })}
      </div>
    );
  }

  // Fallback
  return (
    <Input
      aria-label={question.label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={question.placeholder || ''}
      autoFocus
    />
  );
}
